/**
 * PaymentMethodRequiredModal — Standalone intercept modal for payment method capture.
 * 
 * PURPOSE: Drop-in component that can be triggered from anywhere in the app
 * when a paid action is attempted without a payment method on file.
 * 
 * ZERO dependencies on ProfilePage or any other page component.
 * Manages its own SetupIntent lifecycle internally.
 * 
 * PROPS:
 *   isOpen: boolean — controls visibility
 *   onSuccess: () => void — called after payment method is saved successfully
 *   onDismiss: () => void — called if user closes without completing
 * 
 * FLOW:
 * 1. On mount (when isOpen=true), calls POST /api/payments/setup-intent
 * 2. Renders Stripe Elements card input with returned client_secret
 * 3. On submit, calls stripe.confirmCardSetup()
 * 4. On success, calls POST /api/payments/confirm-payment-method
 * 5. Fires onSuccess() so the original action can resume
 */

import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from '@/components/ui/button';
import { CreditCard, Lock, Loader2, AlertCircle, ShieldCheck } from 'lucide-react';
import { xhrFetchAuthPost, xhrFetchAuth } from '@/lib/xhrFetch';
import { API_URL } from '@/config/api';

let stripePromise = null;

const InlineCardForm = ({ clientSecret, onSuccess, onCancel, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !cardComplete) return;

    setProcessing(true);
    setCardError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement }
      });

      if (error) {
        setCardError(error.message);
        onError(error.message);
        setProcessing(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        // Confirm with backend — attach and set as default
        const confirmRes = await xhrFetchAuthPost(
          `${API_URL}/api/payments/confirm-payment-method`,
          { payment_method_id: setupIntent.payment_method }
        );

        if (!confirmRes.ok) {
          const msg = confirmRes.json?.detail || 'Failed to save payment method';
          setCardError(msg);
          onError(msg);
          setProcessing(false);
          return;
        }

        onSuccess(confirmRes.json);
      } else {
        setCardError(`Setup incomplete: ${setupIntent.status}`);
        setProcessing(false);
      }
    } catch (err) {
      setCardError(err.message || 'An error occurred');
      onError(err.message);
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#e2e8f0',
        '::placeholder': { color: '#64748b' },
        iconColor: '#f59e0b',
      },
      invalid: { color: '#ef4444', iconColor: '#ef4444' },
    },
    hidePostalCode: false,
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-300">
          <Lock className="inline w-4 h-4 mr-2 text-green-400" />
          Card Details
        </label>
        <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600 focus-within:border-amber-500 transition-colors">
          <CardElement
            options={cardElementOptions}
            onChange={(e) => {
              setCardComplete(e.complete);
              setCardError(e.error ? e.error.message : null);
            }}
          />
        </div>
        {cardError && (
          <p className="text-sm text-red-400 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {cardError}
          </p>
        )}
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 text-slate-300"
          data-testid="payment-modal-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || processing || !cardComplete}
          className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900"
          data-testid="payment-modal-save-btn"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <ShieldCheck className="w-4 h-4 mr-2" />
              Save Card & Continue
            </>
          )}
        </Button>
      </div>

      <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1">
        <Lock className="w-3 h-3" />
        Secured by Stripe
      </p>
    </form>
  );
};

const PaymentMethodRequiredModal = ({ isOpen, onSuccess, onDismiss }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  const initSetupIntent = useCallback(async () => {
    setLoading(true);
    setError(null);

    const res = await xhrFetchAuthPost(`${API_URL}/api/payments/setup-intent`, {});

    if (!res.ok) {
      setError(res.json?.detail || 'Failed to initialize payment form');
      setLoading(false);
      return;
    }

    const data = res.json;
    setClientSecret(data.client_secret);

    // Initialize Stripe promise with the publishable key
    if (data.publishable_key && !stripePromise) {
      stripePromise = loadStripe(data.publishable_key);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      initSetupIntent();
    } else {
      // Reset state when closed
      setClientSecret(null);
      setError(null);
      setLoading(true);
    }
  }, [isOpen, initSetupIntent]);

  const handleSuccess = (result) => {
    onSuccess?.(result);
  };

  const handleDismiss = () => {
    onDismiss?.();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleDismiss(); }}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-md" data-testid="payment-method-required-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-center flex items-center justify-center gap-2">
            <CreditCard className="w-5 h-5 text-amber-400" />
            Payment Method Required
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-center">
            A payment method is required to activate additional manuscripts. Your card will not be charged now.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-3" />
              <p className="text-slate-400 text-sm">Initializing secure payment form...</p>
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <p className="text-red-400 mb-4">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button variant="ghost" onClick={handleDismiss} className="text-slate-300">
                  Close
                </Button>
                <Button onClick={initSetupIntent} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                  Try Again
                </Button>
              </div>
            </div>
          ) : clientSecret && stripePromise ? (
            <Elements stripe={stripePromise}>
              <InlineCardForm
                clientSecret={clientSecret}
                onSuccess={handleSuccess}
                onCancel={handleDismiss}
                onError={setError}
              />
            </Elements>
          ) : (
            <div className="text-center py-8 text-slate-400">
              Unable to load payment form. Please try again later.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodRequiredModal;
